import { Command } from 'cmdk';
import {
  CalendarClock,
  LayoutDashboard,
  Moon,
  PenSquare,
  Plus,
  Settings,
  Sun,
  Users,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/primitives';
import { useAccountsStore } from '@/stores/accounts-store';
import { useUiStore } from '@/stores/ui-store';

/**
 * The command palette (⌘K / Ctrl+K).
 *
 * This is the power-user surface: every navigation and every top-level action is
 * reachable from the keyboard without leaving the home row. Connected accounts
 * are listed too, so "post as @foo" is two keystrokes rather than a hunt through
 * the sidebar.
 */
export function CommandMenu() {
  const open = useUiStore((s) => s.commandMenuOpen);
  const setOpen = useUiStore((s) => s.setCommandMenuOpen);
  const setTheme = useUiStore((s) => s.setTheme);

  const navigate = useNavigate();
  const accounts = useAccountsStore((s) => s.accounts);
  const connect = useAccountsStore((s) => s.connect);
  const setSelected = useAccountsStore((s) => s.setSelected);

  // The global hotkey.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        // Otherwise the browser's own "search bookmarks" steals it on some
        // platforms — and Firefox's find-in-page on Ctrl+K.
        event.preventDefault();
        setOpen(!useUiStore.getState().commandMenuOpen);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [setOpen]);

  /** Every action closes the palette first — leaving it open over the destination is jarring. */
  const run = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="overflow-hidden p-0" hideClose>
        {/* Radix requires a title/description for the a11y tree, but showing them
            would waste the top of the palette. Visually hidden, still announced. */}
        <DialogTitle className="sr-only">Command menu</DialogTitle>
        <DialogDescription className="sr-only">
          Search for a page, an account, or an action.
        </DialogDescription>

        <Command
          // cmdk's built-in fuzzy filter. Good enough that a custom scorer would
          // be a downgrade.
          className="[&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium"
          loop
        >
          <div className="flex items-center border-b px-3">
            <Command.Input
              placeholder="Type a command or search…"
              className="placeholder:text-muted-foreground h-12 w-full bg-transparent text-sm outline-none"
            />
          </div>

          <Command.List className="max-h-80 overflow-y-auto p-1.5">
            <Command.Empty className="text-muted-foreground py-8 text-center text-sm">
              Nothing found.
            </Command.Empty>

            <Command.Group heading="Go to">
              <Item icon={LayoutDashboard} onSelect={() => run(() => navigate('/'))}>
                Dashboard
              </Item>
              <Item icon={PenSquare} onSelect={() => run(() => navigate('/compose'))}>
                Compose a post
              </Item>
              <Item icon={CalendarClock} onSelect={() => run(() => navigate('/queue'))}>
                Queue
              </Item>
              <Item icon={Users} onSelect={() => run(() => navigate('/accounts'))}>
                Accounts
              </Item>
              <Item icon={Settings} onSelect={() => run(() => navigate('/settings'))}>
                Settings
              </Item>
            </Command.Group>

            {accounts.length > 0 && (
              <Command.Group heading="Compose as">
                {accounts
                  .filter((account) => account.status === 'active')
                  .map((account) => (
                    <Command.Item
                      key={account.id}
                      // The username is in the value so typing "@foo" matches —
                      // cmdk only searches `value`, not the rendered children.
                      value={`compose ${account.username} ${account.displayName ?? ''}`}
                      onSelect={() =>
                        run(() => {
                          setSelected([account.id]);
                          navigate('/compose');
                        })
                      }
                      className={itemClass}
                    >
                      <Avatar className="size-5">
                        <AvatarImage src={account.avatarUrl ?? undefined} alt="" />
                        <AvatarFallback className="text-[9px]">
                          {account.username.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span>Post as @{account.username}</span>
                    </Command.Item>
                  ))}
              </Command.Group>
            )}

            <Command.Group heading="Actions">
              <Item icon={Plus} onSelect={() => run(() => void connect('/accounts'))}>
                Connect an X account
              </Item>
              <Item icon={Sun} onSelect={() => run(() => setTheme('light'))}>
                Switch to light theme
              </Item>
              <Item icon={Moon} onSelect={() => run(() => setTheme('dark'))}>
                Switch to dark theme
              </Item>
            </Command.Group>
          </Command.List>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

const itemClass =
  'flex cursor-pointer select-none items-center gap-2.5 rounded-md px-3 py-2 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg]:size-4 [&_svg]:text-muted-foreground';

function Item({
  icon: Icon,
  children,
  onSelect,
}: {
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  onSelect: () => void;
}) {
  return (
    <Command.Item value={String(children)} onSelect={onSelect} className={itemClass}>
      <Icon />
      <span>{children}</span>
    </Command.Item>
  );
}
